import { safeJsonParse } from '../utilities/safeJsonParse';

import { fetchMetaobjectsByIds } from './queries/fetchMetaobjectsById';
import { fetchProductVariantsById } from './queries/fetchProductVariantsById';

/**
 * Generic helper for batch fetching and mapping metaobjects by IDs.
 * @param {Object} options - Options object.
 * @param {Array<string>} options.ids - Array of IDs to fetch.
 * @param {Function} options.fetchFn - Fetch function to use (should return Promise<Array<Object>>).
 * @param {Function} [options.mapFn] - Optional mapping function to apply to results.
 * @param {string} [options.metaObjectType] - Optional metaobject type to use in error messages.
 * @returns {Promise<Array<Object>>} - Resolves with fetched (and mapped) objects.
 */
async function batchFetchAndMap({ ids, fetchFn, mapFn, metaObjectType }) {
  const uniqueIds = Array.from(new Set(ids)).filter(Boolean);
  if (uniqueIds.length === 0) return [];
  const results = await fetchFn({ ids: uniqueIds, metaObjectType });
  return mapFn ? results.map(mapFn) : results;
}

/**
 * Recursively fetches and maps all personalisation-related metaobjects into objects keyed by gid.
 * @param {Object} options - Options object.
 * @param {Object} options.personalisation - The root personalisation object (flattened).
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<Object>} - An object containing objects for steps, slots, addon groups, addon items, and component groups.
 */
export async function gatherRawPersonalisationData({ personalisation, country }) {
  if (!Array.isArray(personalisation.steps) && personalisation.steps.length === 0) {
    return {
      personalisationSteps: [],
      personalisationSlots: [],
      addonGroups: [],
      addonItems: [],
      componentGroups: []
    };
  }

  // Fetch all step metaobjects in one request.
  const personalisationSteps = await batchFetchAndMap({
    ids: personalisation.steps,
    metaObjectType: 'step',
    fetchFn: fetchMetaobjectsByIds({ country }),
    mapFn: (step) => ({
      ...step,
      slots: safeJsonParse({ jsonString: step.slots })
    })
  });

  // Gather all slot and addon group IDs from steps for further batch fetching.
  const allSlotIds = getAllSlotIdsFromSteps({ steps: personalisationSteps });
  const allAddonGroupIds = getAllAddonGroupIdsFromSteps({ steps: personalisationSteps });
  // Recursively batch fetch slots and addon groups, merging their results.
  const [{ slots: personalisationSlots, componentGroups, productVariants }, { addonGroups, addonItems }] = await Promise.all([
    fetchAndMapSlots({ slotIds: allSlotIds, personalisationSteps, country }),
    fetchAndMapAddonGroups({ groupIds: allAddonGroupIds, country })
  ]);
  // Return all populated maps.
  return {
    personalisationSteps,
    personalisationSlots,
    addonGroups,
    addonItems,
    componentGroups,
    productVariants
  };
}

/**
 * Extracts all slot IDs from an array of step metaobjects.
 * @param {Object} options - Options object.
 * @param {Array<Object>} options.steps - Array of step metaobjects.
 * @returns {Array<string>} - Array of slot IDs.
 */
function getAllSlotIdsFromSteps({ steps }) {
  // Use flatMap to collect all slot arrays into a single array.
  return steps.flatMap((step) => (Array.isArray(step.slots) ? step.slots : []));
}

/**
 * Extracts all addon group IDs from an array of step metaobjects.
 * @param {Object} options - Options object.
 * @param {Array<Object>} options.steps - Array of step metaobjects.
 * @returns {Array<string>} - Array of addon group IDs.
 */
function getAllAddonGroupIdsFromSteps({ steps }) {
  // Map each step to its addonGroup property, filtering out falsy values.
  return steps.map((step) => step.addonGroup).filter(Boolean);
}

/**
 * Batch fetches slots and recursively their component groups. Returns new maps.
 * @param {Object} options - Options object.
 * @param {Array<string>} options.slotIds - Array of slot IDs to fetch.
 * @param {Array<Object>} options.personalisationSteps - Array of personalisation step metaobjects.
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<{componentGroups: Array<Object>, slots: Array<Object>, productVariants: Array<Object>}>} Resolves with new maps.
 */
async function fetchAndMapSlots({ slotIds, personalisationSteps, country }) {
  const slots = await batchFetchAndMap({
    ids: slotIds,
    metaObjectType: 'slot',
    fetchFn: fetchMetaobjectsByIds({ country }),
    mapFn: (slot) => {
      const { id: stepId, slotMode } = personalisationSteps.find((stepItem) => stepItem.slots.includes(slot.id));
      return {
        ...slot,
        stepId,
        slotMode
      };
    }
  });

  // Gather all component group IDs from slots for further batch fetching.
  const allComponentGroupIds = getAllComponentGroupIdsFromSlots({ slots });
  const componentGroups = (await fetchAndMapComponentGroups({
    groupIds: allComponentGroupIds,
    country
  }))
    .map(({ variants, ...rest }) => ({
      ...rest,
      variants: safeJsonParse({ jsonString: variants })
    }))

  const productVariantIds = componentGroups.flatMap(({ variants, id }) => variants.map((productVariantId) => ({ productVariantId, groupId: id })));
  // Add the group ID to the product variant so we can match it to the correct group.
  // @TODO: OLD CODE - CHECK WITH JIM IF THIS IS CORRECT
  // const productVariants = (await fetchProductVariantsById({ ids: productVariantIds.map(({ productVariantId }) => productVariantId) }))
  //   .map((productVariant) => ({
  //     ...productVariant,
  //     groupId: productVariantIds.find(({ productVariantId }) => productVariantId === productVariant.id)?.groupId
  //   }));

  const productVariants = (await fetchProductVariantsById({
    ids: productVariantIds.map(({ productVariantId }) => productVariantId),
    country
  }))
    .filter(productVariant => productVariant && productVariant.id)
    .map((productVariant) => ({
      ...productVariant,
      groupId: productVariant?.id ? productVariantIds.find(({ productVariantId }) => productVariantId === productVariant.id)?.groupId : null
    }));

  return { componentGroups, slots, productVariants };
}

/**
 * Extracts all allowed component group IDs from an array of slot metaobjects.
 * @param {Object} options - Options object.
 * @param {Array<Object>} options.slots - Array of slot metaobjects.
 * @returns {Array<string>} - Array of component group IDs.
 */
function getAllComponentGroupIdsFromSlots({ slots }) {
  // Use flatMap to collect all allowedComponentGroups arrays into a single array.
  return slots.flatMap((slot) => {
    const parsedAllowedComponentGroups = safeJsonParse({ jsonString: slot.allowedComponentGroups || '[]' });
    return Array.isArray(parsedAllowedComponentGroups) ? parsedAllowedComponentGroups : [];
  });
}

/**
 * Batch fetches addon groups and recursively their addon items. Returns new maps.
 * @param {Object} options - Options object.
 * @param {Array<string>} options.groupIds - Array of addon group IDs to fetch.
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<{addonGroups: Object, addonItems: Object}>} Resolves with new maps.
 */
async function fetchAndMapAddonGroups({ groupIds, country }) {
  const addonGroups = await batchFetchAndMap({
    ids: groupIds,
    metaObjectType: 'addon-group',
    fetchFn: fetchMetaobjectsByIds({ country })
  });
  // Gather all addon item IDs from groups for further batch fetching.
  const allAddonItemIds = getAllAddonItemIdsFromGroups({ groups: addonGroups });
  const addonItems = await fetchAndMapAddonItems({ itemIds: allAddonItemIds, country });
  return { addonGroups, addonItems };
}

/**
 * Extracts all addon item IDs from an array of addon group metaobjects.
 * @param {Object} options - Options object.
 * @param {Array<Object>} options.groups - Array of addon group metaobjects.
 * @returns {Array<string>} - Array of addon item IDs.
 */
function getAllAddonItemIdsFromGroups({ groups }) {
  // Use flatMap to collect all addonItems arrays into a single array.
  return groups.flatMap((group) => (Array.isArray(group.addonItems) ? group.addonItems : []));
}

/**
 * Batch fetches addon items. Returns a new map.
 * @param {Object} options - Options object.
 * @param {Array<string>} options.itemIds - Array of addon item IDs to fetch.
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<Object>} Resolves with a new map of addon items.
 */
function fetchAndMapAddonItems({ itemIds, country }) {
  return batchFetchAndMap({
    ids: itemIds,
    metaObjectType: 'addon-item',
    fetchFn: fetchMetaobjectsByIds({ country })
  });
}

/**
 * Batch fetches component groups. Returns a new map.
 * @param {Object} options - Options object.
 * @param {Array<string>} options.groupIds - Array of component group IDs to fetch.
 * @param {string} options.country - The country code of the user.
 * @returns {Promise<Array<Object>>} Resolves with a new map of component groups.
 */
function fetchAndMapComponentGroups({ groupIds, country }) {
  return batchFetchAndMap({
    ids: groupIds,
    metaObjectType: 'component-group',
    fetchFn: fetchMetaobjectsByIds({ country })
  });
}
